import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import {
  RootState,
  campaignSelector,
  dashboardSelector,
  ownerSelector,
} from "../../store/store";
import axios from "../../utils/axios";
import {
  buildEngagementAnalyticsChartData,
  type EngagementAnalyticsRow,
  engagementAnalyticsRowsToCsv,
} from "../../utils/engagementChart";
import { fetchUserOwnersWithWorkspaces } from "../../utils";
import { setSelectedCampaign, setSingleCampaign } from "../../store/slices/campaignSlice";
import { FaChevronDown } from "react-icons/fa6";
import { HiMiniLink } from "react-icons/hi2";
import { MdOutlineMail, MdOutlinePersonAddAlt1 } from "react-icons/md";
import { TbMessage2 } from "react-icons/tb";
import DashboardCard from "./widgets/DashboardCard";
import KpiStatCard from "./widgets/KpiStatCard";
import Select from "../ui/Select";
import EngagementAnalyticsGraph from "./widgets/EngagementAnalyticsGraph";
import {
  ANALYTICS_CHART_ICON,
  CAMPAIGN_ONBOARDING_ICON,
  LINKEDIN_ONBOARDING_ICON,
} from "../assets";

const DashboardMain = () => {
  const { owner, campaigns, currentWorkspace, currentUser } =
    useSelector(ownerSelector);

  const displayName = useMemo(() => {
    const first = String(currentUser?.firstName || "").trim();
    const last = String(currentUser?.lastName || "").trim();
    const full = [first, last].filter(Boolean).join(" ").trim();
    return full || String(owner?.name || "").trim();
  }, [currentUser?.firstName, currentUser?.lastName, owner?.name]);
  const workspace =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace || "null");
          } catch {
            return null;
          }
        })()
      : currentWorkspace;
  const workspaceId = workspace?._id as string;
  const userId = currentUser?.userId as string;
  const { singleCampaign, selectedCampaign } = useSelector(campaignSelector);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const customRangePopoverRef = useRef<HTMLDivElement | null>(null);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState<"week" | "month" | "custom">("week");
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [showCustomRangeCalendar, setShowCustomRangeCalendar] = useState(false);
  const [downloadOption, setDownloadOption] = useState<string>("");
  const [selectedSenderOwnerId, setSelectedSenderOwnerId] = useState<string>("ALL");
  const [senderAccounts, setSenderAccounts] = useState<
    Array<{
      ownerId: string;
      name: string;
      email: string;
      hasLinkedinToken: boolean;
      isDisconnected: boolean;
    }>
  >([]);
  const [engagementChartData, setEngagementChartData] = useState<EngagementAnalyticsRow[]>([]);
  const [allCampaignData, setAllCampaignData] = useState<any | null>(null);
  const [isOnboardingExpanded, setIsOnboardingExpanded] = useState(true);
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const navigate = useNavigate();

  const availableOwnerEntries = useMemo(() => {
    const fromApi = senderAccounts.map((entry) => ({
      ownerId: String(entry.ownerId || ""),
      name: String(entry.name || "").trim(),
      email: String(entry.email || "").trim(),
      hasLinkedinToken: Boolean(entry.hasLinkedinToken),
      isDisconnected: Boolean(entry.isDisconnected),
    }));
    const deduped = new Map<
      string,
      {
        ownerId: string;
        name: string;
        email: string;
        hasLinkedinToken: boolean;
        isDisconnected: boolean;
      }
    >();
    fromApi.forEach((entry) => {
      if (!entry.ownerId || entry.isDisconnected) return;
      if (!deduped.has(entry.ownerId)) {
        deduped.set(entry.ownerId, entry);
      }
    });
    return Array.from(deduped.values());
  }, [senderAccounts]);

  const senderOptions = useMemo(
    () => [
      { value: "ALL", label: "All senders" },
      ...availableOwnerEntries
        .filter((entry) => entry.hasLinkedinToken)
        .map((entry) => ({
          value: entry.ownerId,
          label: entry.name || entry.email || "Sender",
        })),
    ],
    [availableOwnerEntries]
  );

  const ownerFilterIds = useMemo(() => {
    if (selectedSenderOwnerId !== "ALL") return [selectedSenderOwnerId];
    const apiIds = availableOwnerEntries
      .filter((entry) => entry.hasLinkedinToken && !entry.isDisconnected)
      .map((entry) => String(entry.ownerId || "").trim())
      .filter(Boolean);
    if (apiIds.length > 0) return apiIds;
    const fallbackOwnerId = String(owner?._id || "").trim();
    return fallbackOwnerId ? [fallbackOwnerId] : [];
  }, [selectedSenderOwnerId, availableOwnerEntries, owner?._id]);

  const filteredCampaigns = useMemo(() => {
    if (!campaigns?.length) return [];
    const ids = ownerFilterIds;
    if (!ids.length) return [];
    return campaigns.filter((campaign: any) => {
      const campaignOwnerId = String(campaign?.ownerId?._id || campaign?.ownerId || "");
      return ids.includes(campaignOwnerId);
    });
  }, [campaigns, ownerFilterIds]);

  useEffect(() => {
    const loadSenderAccounts = async () => {
      if (!userId && !owner?._id) return;
      try {
        const response = await fetchUserOwnersWithWorkspaces(
          1,
          100,
          "",
          userId,
          workspaceId,
          owner?._id
        );
        const accounts = Array.isArray(response?.accounts) ? response.accounts : [];
        setSenderAccounts(
          accounts.map((entry: any) => ({
            ownerId: String(entry?._id || ""),
            name: String(entry?.name || "").trim(),
            email: String(entry?.email || "").trim(),
            hasLinkedinToken: Boolean(entry?.hasLinkedinToken),
            isDisconnected: Boolean(entry?.isDisconnected),
          }))
        );
      } catch {
        setSenderAccounts([]);
      }
    };
    loadSenderAccounts();
  }, [userId, workspaceId, owner?._id]);

  useEffect(() => {
    const senderSet = new Set(senderOptions.map((opt) => opt.value));
    if (!senderSet.has(selectedSenderOwnerId)) {
      setSelectedSenderOwnerId("ALL");
    }
  }, [senderOptions, selectedSenderOwnerId]);

  const campaignOptions = useMemo(
    () => [
      { value: "ALL", label: "All campaigns" },
      ...filteredCampaigns.map((campaign: any) => ({
        value: campaign._id,
        label: campaign.campaignName,
      })),
    ],
    [filteredCampaigns]
  );

  const normalizedSelectedCampaign = useMemo(() => {
    const current = String(selectedCampaign || "").trim();
    if (!current) return "ALL";
    return campaignOptions.some((option) => option.value === current) ? current : "ALL";
  }, [selectedCampaign, campaignOptions]);

  const handleDownloadChartSvg = () => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const svgElement = chartContainer.querySelector("svg");
    if (!svgElement) {
      toast.error("Unable to download chart right now.");
      return;
    }

    const serializer = new XMLSerializer();
    const svgContent = serializer.serializeToString(svgElement);
    const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "engagement-analytics.svg";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleDownloadChartPng = () => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const svgElement = chartContainer.querySelector("svg");
    if (!svgElement) {
      toast.error("Unable to download chart right now.");
      return;
    }

    const serializer = new XMLSerializer();
    let svgContent = serializer.serializeToString(svgElement);
    if (!svgContent.includes("xmlns=")) {
      svgContent = svgContent.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }

    const svgBlob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width || 1200;
      canvas.height = image.height || 400;
      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(svgUrl);
        toast.error("Unable to prepare PNG export.");
        return;
      }

      // Use design-system token so export background stays consistent with UI.
      const cssWhite =
        window.getComputedStyle(document.documentElement).getPropertyValue("--color-white").trim() || "white";
      context.fillStyle = cssWhite;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      URL.revokeObjectURL(svgUrl);

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error("Unable to generate PNG file.");
          return;
        }
        const pngUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = pngUrl;
        anchor.download = "engagement-analytics.png";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(pngUrl);
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      toast.error("Unable to convert chart to PNG.");
    };

    image.src = svgUrl;
  };

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (analyticsTimeRange !== "custom") return;
      const container = customRangePopoverRef.current;
      if (!container) return;
      if (!container.contains(event.target as Node)) {
        setShowCustomRangeCalendar(false);
      }
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, [analyticsTimeRange]);

  useEffect(() => {
    (async () => {
      try {
        if (!selectedCampaign || selectedCampaign === "ALL") return;
        const { data } = await axios.post("/campaign/getCampaignData", {
          userId,
          workspaceId,
          campaignId: selectedCampaign,
        });
        // console.log(data);
        dispatch(setSingleCampaign(data.campaign));
      } catch (error) {
        toast.error("Error fetching campaign data");
        console.log(error);
      }
    })();
  }, [dispatch, selectedCampaign, owner?._id, userId, workspaceId]);

  useEffect(() => {
    if (!filteredCampaigns?.length) {
      if (selectedCampaign !== "ALL") dispatch(setSelectedCampaign("ALL"));
      return;
    }
    const validCampaignIds = new Set(filteredCampaigns.map((c: any) => String(c._id)));
    const isValidSelection = selectedCampaign === "ALL" || validCampaignIds.has(String(selectedCampaign));
    if (!isValidSelection) {
      dispatch(setSelectedCampaign("ALL"));
    }
  }, [filteredCampaigns, selectedCampaign, dispatch]);

  useEffect(() => {
    (async () => {
      if (selectedCampaign !== "ALL") {
        setAllCampaignData(null);
        return;
      }
      if (!filteredCampaigns?.length) {
        setAllCampaignData({
          prospects: [],
          prospectsWiseCampaignActions: [],
        });
        return;
      }
      try {
        const responses = await Promise.all(
          filteredCampaigns.map((campaign: any) =>
            axios.post("/campaign/getCampaignData", {
              userId,
              workspaceId,
              campaignId: campaign._id,
            })
          )
        );
        const allCampaigns = responses.map((resp) => resp?.data?.campaign).filter(Boolean);
        setAllCampaignData({
          prospects: allCampaigns.flatMap((c: any) => c?.prospects ?? []),
          prospectsWiseCampaignActions: allCampaigns.flatMap(
            (c: any) => c?.prospectsWiseCampaignActions ?? []
          ),
        });
      } catch (error) {
        console.log(error);
        toast.error("Error fetching all campaigns data");
        setAllCampaignData({
          prospects: [],
          prospectsWiseCampaignActions: [],
        });
      }
    })();
  }, [selectedCampaign, filteredCampaigns, userId, workspaceId]);
  const activeCampaigns = useMemo(
    () => campaigns?.filter((campaign: any) => campaign.campaignStatus === "ACTIVE").length ?? 0,
    [campaigns]
  );

  const connectedSenders = useMemo(
    () =>
      availableOwnerEntries.filter(
        (entry) => entry.hasLinkedinToken && !entry.isDisconnected
      ),
    [availableOwnerEntries]
  );

  const hasConnectedSenders = connectedSenders.length > 0;

  const completedOnboardingSteps = useMemo(() => {
    let done = 0;
    if (hasConnectedSenders) done += 1;
    if (activeCampaigns > 0) done += 1;
    return done;
  }, [hasConnectedSenders, activeCampaigns]);

  const metricsSourceCampaign = selectedCampaign === "ALL" ? allCampaignData : singleCampaign;
  const totalProspects = metricsSourceCampaign?.prospectsWiseCampaignActions?.length ?? 0;
  const prospectActions = useMemo(
    () => metricsSourceCampaign?.prospectsWiseCampaignActions ?? [],
    [metricsSourceCampaign?.prospectsWiseCampaignActions]
  );

  // Match `LinkedinGraphs`: invitations count prospects with a completed `invitation` action (not `invitationWithDelay` in the old filter).
  const invitationSent = useMemo(
    () =>
      prospectActions.filter(
        (prospect: any) =>
          (prospect?.campaignActions?.filter(
            (action: any) => action.type === "invitation" && action.done === "DONE"
          ).length ?? 0) > 0
      ).length,
    [prospectActions]
  );

  const acceptedInvitations = useMemo(
    () => prospectActions.filter((prospect: any) => prospect.connected === "ACCEPTED").length ?? 0,
    [prospectActions]
  );

  const replied = useMemo(
    () =>
      prospectActions.filter((prospect: any) =>
        prospect?.campaignActions?.some((action: any) => action.done === "HASREPLIED")
      ).length,
    [prospectActions]
  );

  const messagesSent = useMemo(
    () =>
      prospectActions.reduce((sum: number, prospect: any) => {
        const sentCount =
          prospect?.campaignActions?.filter(
            (action: any) =>
              (action.type === "message" || action.type === "messageWithDelay") &&
              action.done === "DONE"
          ).length ?? 0;
        return sum + sentCount;
      }, 0),
    [prospectActions]
  );

  const visitStats = useMemo(() => {
    const allVisitActions = prospectActions.flatMap((prospect: any) =>
      (prospect?.campaignActions ?? []).filter(
        (action: any) => action.type === "visit" || action.type === "visitWithDelay"
      )
    );
    const done = allVisitActions.filter((action: any) => action.done === "DONE").length;
    const total = allVisitActions.length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    return { done, total, percent };
  }, [prospectActions]);

  const pendingConnections = useMemo(
    () => prospectActions.filter((p: any) => p.connected === "PENDING").length ?? 0,
    [prospectActions]
  );

  useEffect(() => {
    const resolveRange = () => {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      if (analyticsTimeRange === "week") {
        const start = new Date(end);
        start.setDate(end.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      if (analyticsTimeRange === "month") {
        const start = new Date(end);
        start.setDate(end.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const customEnd = new Date(customEndDate);
        customEnd.setHours(23, 59, 59, 999);
        start.setHours(0, 0, 0, 0);
        return { start, end: customEnd };
      }
      return null;
    };

    const range = resolveRange();
    if (!range) {
      // In custom mode, don't refresh chart until full range is selected.
      if (analyticsTimeRange !== "custom") {
        setEngagementChartData([]);
      }
      return;
    }

    (async () => {
      try {
        const payload: any = {
          userId,
          workspaceId,
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
        };
        if (ownerFilterIds.length) payload.ownerIds = ownerFilterIds;
        if (selectedCampaign !== "ALL") payload.campaignId = selectedCampaign;
        const { data } = await axios.post("/campaign/getCampaignEngagementAnalytics", payload);
        if (Array.isArray(data?.rows)) {
          setEngagementChartData(data.rows);
          return;
        }
        setEngagementChartData([]);
      } catch (error) {
        console.log(error);
        // Safe fallback to previous synthetic distribution if API fails.
        const fallback = buildEngagementAnalyticsChartData(analyticsTimeRange, {
          connectionsSent: invitationSent,
          connectionsAccepted: acceptedInvitations,
          messagesSent,
          messageReplies: replied,
          profileVisits: visitStats.done,
          pendingConnections,
        }, {
          startDate: customStartDate,
          endDate: customEndDate,
        });
        setEngagementChartData(fallback);
      }
    })();
  }, [
    analyticsTimeRange,
    customStartDate,
    customEndDate,
    selectedCampaign,
    ownerFilterIds,
    userId,
    workspaceId,
    invitationSent,
    acceptedInvitations,
    messagesSent,
    replied,
    visitStats.done,
    pendingConnections,
  ]);

  const handleDownloadChartCsv = () => {
    if (!engagementChartData.length) {
      toast.error("No data to export.");
      return;
    }
    const csv = engagementAnalyticsRowsToCsv(engagementChartData);
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "engagement-analytics.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const pctOfProspects = (n: number) =>
    totalProspects > 0 ? Math.round((n / totalProspects) * 100) : 0;

  const profileVisitsInSelectedRange = useMemo(
    () =>
      (engagementChartData || []).reduce(
        (sum, row) => sum + Number(row?.profileVisits || 0),
        0
      ),
    [engagementChartData]
  );

  const invitationPct = pctOfProspects(invitationSent);
  const acceptancePct = pctOfProspects(acceptedInvitations);
  const responseRatePct = pctOfProspects(replied);
  const messagePct = pctOfProspects(messagesSent);
  const profileVisitsPct = pctOfProspects(profileVisitsInSelectedRange);

  const kpis = [
    {
      label: "Profile visits",
      // Keep card aligned with chart range/API rows.
      value: profileVisitsInSelectedRange,
      growth: `${profileVisitsPct}%`,
      helper:
        profileVisitsInSelectedRange > 0
          ? `From ${profileVisitsInSelectedRange} visit actions in selected range`
          : "No visit actions in selected range",
      icon: <HiMiniLink size={14} className="text-mainColor" />,
    },
    {
      label: "Connection requests sent",
      value: invitationSent,
      growth: `${invitationPct}%`,
      helper: `From ${totalProspects} prospects in campaign`,
      icon: <MdOutlinePersonAddAlt1 size={14} className="text-mainColor" />,
    },
    {
      label: "Connection acceptance rate",
      value: acceptedInvitations,
      growth: `${acceptancePct}%`,
      helper: `From ${totalProspects} prospects in campaign`,
      icon: <MdOutlinePersonAddAlt1 size={14} className="text-mainColor" />,
    },
  
    // {
    //   label: "Reply rate",
    //   value: `${responseRatePct}%`,
    //   helper: `Share of ${totalProspects} prospects`,
    //   icon: <TbMessage2 size={14} className="text-mainColor" />,
    // },
    // {
    //   label: "Replies received",
    //   value: replied,
    //   growth: `${responseRatePct}%`,
    //   helper: `From ${totalProspects} prospects in campaign`,
    //   icon: <TbMessage2 size={14} className="text-mainColor" />,
    // },
    {
      label: "Messages sent",
      value: messagesSent,
      growth: `${messagePct}%`,
      helper: `From ${totalProspects} prospects in campaign`,
      icon: <MdOutlineMail size={14} className="text-mainColor" />,
    },
   
  ];

  return (
    <div className="font-inter w-full space-y-3 pb-4">
      <DashboardCard className="p-4">
        <div
          className={`flex items-center justify-between ${isOnboardingExpanded ? "mb-4" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-light-grey text-xs font-medium text-dark-grey">
              {completedOnboardingSteps}/2
            </div>
            <div>
              <h2 className="flex flex-wrap items-center gap-1 text-lg font-semibold leading-6 text-dark-black sm:text-xl">
                {`Let's get started ${displayName}`.trim()}
                <span>👋</span>
              </h2>
              <p className="text-sm leading-5 text-dark-grey">Follow these steps to get the most out of Innlead</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsOnboardingExpanded((prev) => !prev)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-light-grey text-dark-grey transition-colors hover:bg-[#f8fafc]"
            aria-expanded={isOnboardingExpanded}
            aria-label={
              isOnboardingExpanded
                ? "Collapse getting started steps"
                : "Expand getting started steps"
            }
          >
            <FaChevronDown
              size={16}
              className={`transition-transform duration-200 ${
                isOnboardingExpanded ? "" : "-rotate-90"
              }`}
            />
          </button>
        </div>

        {isOnboardingExpanded ? (
        <div className="space-y-2">
          <div className="flex flex-col gap-3 rounded-[8px] border border-light-grey p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <div className="shrink-0 rounded-full border border-light-grey p-2">
                <img src={LINKEDIN_ONBOARDING_ICON} alt="LinkedIn" className="h-4 w-4" />
              </div>
              <div>
                <p className="text-base font-semibold leading-6 text-dark-black">
                  {hasConnectedSenders
                    ? `Welcome Back ${displayName || "there"}`
                    : "Connect your LinkedIn account"}
                </p>
                <p className="text-sm leading-5 text-dark-grey">
                  {hasConnectedSenders
                    ? connectedSenders.length === 1
                      ? "Your LinkedIn sender is connected and ready to use."
                      : `${connectedSenders.length} LinkedIn senders are connected and ready to use.`
                    : "Unlock most powerful features by connecting your account"}
                </p>
              </div>
            </div>
            {!hasConnectedSenders ? (
              <button
                onClick={() => navigate("/teams")}
                className="w-full shrink-0 rounded border border-light-grey px-3 py-2 text-sm text-dark-black sm:w-auto"
              >
                + Connect now
              </button>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 rounded-[8px] border border-light-grey p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <div className="shrink-0 rounded-full border border-light-grey p-2">
                <img src={CAMPAIGN_ONBOARDING_ICON} alt="Create campaign" className="h-4 w-4" />
              </div>
              <div>
                <p className="text-base font-semibold leading-6 text-dark-black">Create a campaign</p>
                <p className="text-sm leading-5 text-dark-grey">Import or find leads and create a sequence to book meetings</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/campaign-management")}
              className="w-full shrink-0 rounded border border-light-grey px-3 py-2 text-sm text-dark-black sm:w-auto"
            >
              + Create a campaign
            </button>
          </div>
        </div>
        ) : null}
      </DashboardCard>

      <DashboardCard className="p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            <img src={ANALYTICS_CHART_ICON} alt="Analytics icon" className="mt-0.5 h-7 w-7" />
            <div>
            <h3 className="text-xl font-semibold leading-6 text-dark-black">Analytics</h3>
            <p className="text-sm leading-5 text-dark-grey">Monitor the results of your campaign</p>
            </div>
          </div>
          <div
            className="relative"
            ref={customRangePopoverRef}
            onMouseDownCapture={() => {
              if (analyticsTimeRange === "custom") {
                setShowCustomRangeCalendar(true);
              }
            }}
          >
            <Select
              variant="compact"
              size="sm"
              value={analyticsTimeRange}
              onChange={(value) => {
                const nextRange = value === "month" || value === "custom" ? value : "week";
                setAnalyticsTimeRange(nextRange);
                setShowCustomRangeCalendar(nextRange === "custom");
              }}
              options={[
                { value: "week", label: "This week" },
                { value: "month", label: "This month" },
                { value: "custom", label: "Custom range" },
              ]}
            />
            {analyticsTimeRange === "custom" && showCustomRangeCalendar ? (
              <div className="absolute right-0 top-[calc(100%+8px)] z-40 rounded-[8px] border border-light-grey bg-white p-2 shadow-lg">
                <DatePicker
                  selected={customStartDate}
                  onChange={(dates) => {
                    const [start, end] = (dates as [Date | null, Date | null]) ?? [null, null];
                    setCustomStartDate(start);
                    setCustomEndDate(end);
                    if (start && end) {
                      setShowCustomRangeCalendar(false);
                    }
                  }}
                  startDate={customStartDate}
                  endDate={customEndDate}
                  selectsRange
                  inline
                  calendarClassName="ls-datepicker-calendar"
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Select
            size="sm"
            label="All senders"
            value={selectedSenderOwnerId}
            onChange={(value) => {
              setSelectedSenderOwnerId(value);
              if (selectedCampaign === "ALL") return;

              // Business rule: choosing "All senders" always resets campaign filter.
              if (value === "ALL") {
                dispatch(setSelectedCampaign("ALL"));
                return;
              }

              // Preserve campaign only when it belongs to the selected sender.
              const selectedCampaignDoc = (campaigns || []).find(
                (campaign: any) => String(campaign?._id || "") === String(selectedCampaign)
              );
              const selectedCampaignOwnerId = String(
                selectedCampaignDoc?.ownerId?._id || selectedCampaignDoc?.ownerId || ""
              );
              if (!selectedCampaignOwnerId || selectedCampaignOwnerId !== value) {
                dispatch(setSelectedCampaign("ALL"));
              }
            }}
            options={senderOptions}
          />
          <Select
            size="sm"
            label="All campaigns"
            value={normalizedSelectedCampaign}
            onChange={(value) => dispatch(setSelectedCampaign(value))}
            options={campaignOptions}
          />
        </div>
      </DashboardCard>

      <section className="grid grid-cols-1 gap-[11px] md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {kpis.map((kpi) => (
          <KpiStatCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            growth={kpi.growth}
            helper={kpi.helper}
            icon={kpi.icon}
          />
        ))}
      </section>

      <DashboardCard className="p-3.5">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-bold uppercase tracking-wide text-dark-black">Engagement Analytics</p>
          <Select
            variant="compact"
            size="sm"
            value={downloadOption}
            label="Download"
            placeholder="Download"
            options={[
              { value: "svg", label: "Download SVG" },
              { value: "png", label: "Download PNG" },
              { value: "csv", label: "Download CSV" },
            ]}
            onChange={(value) => {
              if (value === "svg") handleDownloadChartSvg();
              if (value === "png") handleDownloadChartPng();
              if (value === "csv") handleDownloadChartCsv();
              // Reset back to "Download" so it stays a single-action control.
              setDownloadOption("");
            }}
          />
        </div>
        <EngagementAnalyticsGraph chartData={engagementChartData} containerRef={chartContainerRef} />
      </DashboardCard>
    </div>
  );
};

export default DashboardMain;
